/* eslint-disable react/prop-types */
import clsx from 'clsx'
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { Dropdown } from 'react-bootstrap'
import { ACTION_DEFINITIONS } from './actionDefinitions'
import { fitMenuToViewport } from './fitMenuToViewport'
import './PopoverMenu.scss'
import PopoverMenuGroup from './PopoverMenuGroup'

// `fixed` lets the menu escape scroll containers it sits in (e.g. the Engage table's
// `.Uic-Table-wrapper`), which otherwise clip it to the table's height. The page can't be scrolled
// to a `fixed` menu, so `fitMenuToViewport` keeps it on screen instead (Popper's `flip` is off).
const MENU_POPPER_CONFIG = { strategy: 'fixed', modifiers: [fitMenuToViewport] }

/** @type {Map<string, (open: boolean) => void>} */
const EXCLUSIVE_POPOVER_MENU_REGISTRY = new Map()

function closeOtherExclusivePopoverMenus(keepKey) {
  ;[...EXCLUSIVE_POPOVER_MENU_REGISTRY.entries()].forEach(([key, setOpen]) => {
    if (key !== keepKey) {
      setOpen(false)
      EXCLUSIVE_POPOVER_MENU_REGISTRY.delete(key)
    }
  })
}

/**
 * Merges user config with action definitions
 */
function mergeItemWithDefinition(item) {
  const definition = ACTION_DEFINITIONS[item.id]

  if (!definition) {
    // No definition found - must provide label
    if (!item.label) {
      console.warn(`PopoverMenu: Item "${item.id}" missing required field (label)`)
      return null
    }
    return item
  }

  // Merge definition defaults with item overrides
  const merged = {
    ...definition,
    ...item,
    permissionCheck: item.permissionCheck ?? definition.defaultPermissionCheck,
    availabilityCheck: item.availabilityCheck ?? definition.defaultAvailabilityCheck,
    shouldRender: item.shouldRender ?? definition.defaultShouldRender,
  }

  // Handle dynamic label (e.g., based on integration type)
  if (definition.getLabel && !item.label) {
    merged.label = definition.getLabel(item.context || {})
  } else if (!merged.label) {
    merged.label = definition.label
  }

  if (!merged.icon) {
    merged.icon = definition.icon
  }

  return merged
}

function PopoverMenu(props) {
  const {
    config,
    employee,
    context,
    onAction,
    customActionHandlers,
    toggleButton,
    toggleProps = {},
    menuClassName = 'dropdown-menu-fit dropdown-menu-end dropdown-menu-anim dropdown-menu-top-unround',
    menuWidth = '200px',
    showIcons = false, // Default to hiding icons
    exclusiveMenuKey,
  } = props

  const [showMenu, setShowMenu] = useState(false)
  const menuRef = useRef(null)
  const isExclusive = exclusiveMenuKey != null && exclusiveMenuKey !== ''

  // The menu stays mounted while closed, so it would reopen scrolled to where it was left.
  useLayoutEffect(() => {
    if (showMenu && menuRef.current) {
      menuRef.current.scrollTop = 0
    }
  }, [showMenu])

  useEffect(() => {
    if (!isExclusive) {
      return undefined
    }
    return () => {
      EXCLUSIVE_POPOVER_MENU_REGISTRY.delete(exclusiveMenuKey)
    }
  }, [exclusiveMenuKey, isExclusive])

  const handleExclusiveToggle = useCallback(
    (nextShow) => {
      if (nextShow) {
        closeOtherExclusivePopoverMenus(exclusiveMenuKey)
        EXCLUSIVE_POPOVER_MENU_REGISTRY.set(exclusiveMenuKey, setShowMenu)
      } else {
        EXCLUSIVE_POPOVER_MENU_REGISTRY.delete(exclusiveMenuKey)
      }
      setShowMenu(nextShow)
    },
    [exclusiveMenuKey],
  )

  // Helper function to merge sub-menu items
  const mergeSubMenuItems = React.useCallback(
    (subMenu) => {
      if (!subMenu || !subMenu.items) {
        return subMenu
      }

      return {
        ...subMenu,
        items: subMenu.items
          .map((item) => {
            // Handle falsy values
            if (!item) {
              return null
            }

            // If item is a string, treat it as action ID
            const itemConfig = typeof item === 'string' ? { id: item } : item

            // If item is not an object at this point, skip it
            if (typeof itemConfig !== 'object') {
              return null
            }

            // Add context to item for getLabel functions
            const itemWithContext = {
              ...itemConfig,
              context,
            }

            return mergeItemWithDefinition(itemWithContext)
          })
          .filter(Boolean), // Remove null items
      }
    },
    [context],
  )

  // Merge config with action definitions
  const mergedConfig = useMemo(
    () =>
      config
        .map((group) => ({
          ...group,
          items: group.items
            .map((item) => {
              // Handle falsy values (false, null, undefined) - these are used for conditional rendering
              if (!item) {
                return null
              }

              // handle if item is a string, treat it as action ID
              const itemConfig = typeof item === 'string' ? { id: item } : item
              if (typeof itemConfig !== 'object') {
                return null
              }

              const itemWithContext = {
                ...itemConfig,
                context,
              }

              const mergedItem = mergeItemWithDefinition(itemWithContext)

              if (mergedItem && itemWithContext.subMenu) {
                mergedItem.subMenu = mergeSubMenuItems(itemWithContext.subMenu)
              }

              return mergedItem
            })
            .filter(Boolean), // Remove null items
        }))
        .filter((group) => group.items.length > 0),
    [config, context, mergeSubMenuItems],
  )

  const toggleDomId = toggleProps.id || (isExclusive ? `popover-menu-toggle-${exclusiveMenuKey}` : 'dropdownToggle')

  // Default toggle button
  const defaultToggle = (
    <Dropdown.Toggle
      className={clsx('Uic-Button-base', toggleProps.className || 'removeDownIcon')}
      variant={toggleProps.variant || 'light'}
      id={toggleDomId}
      style={toggleProps.style}
    >
      {toggleProps.icon ? (
        <i className={toggleProps.icon} style={toggleProps.iconStyle} />
      ) : (
        <i className="flaticon-more" />
      )}
    </Dropdown.Toggle>
  )

  return (
    <Dropdown
      drop="down"
      show={showMenu}
      onToggle={isExclusive ? handleExclusiveToggle : setShowMenu}
      onClick={(e) => e.stopPropagation()}
      onMouseLeave={(e) => {
        const { relatedTarget } = e
        // relatedTarget may be a Text node or other non-Element; only Element has .closest().
        if (relatedTarget instanceof Element && relatedTarget.closest('.popover-submenu')) {
          e.preventDefault()
        }
      }}
    >
      {toggleButton || defaultToggle}
      <Dropdown.Menu
        align="end"
        className={clsx('popover-menu-dropdown', menuClassName)}
        flip={false}
        popperConfig={MENU_POPPER_CONFIG}
        ref={menuRef}
      >
        <ul
          className="nav popover-menu-actions-nav"
          style={{ width: menuWidth, marginTop: '0.25rem', marginBottom: '0.25rem' }}
        >
          {mergedConfig.map((group, index) => (
            <React.Fragment key={group.title || `group-${index}`}>
              {index > 0 && (
                <li
                  style={{
                    borderTop: '1px solid var(--bs-border-color)',
                    margin: '0.5rem auto',
                    width: '90%',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                />
              )}
              <PopoverMenuGroup
                group={group}
                employee={employee}
                context={context}
                onAction={onAction}
                customActionHandlers={customActionHandlers}
                showIcons={showIcons}
              />
            </React.Fragment>
          ))}
        </ul>
      </Dropdown.Menu>
    </Dropdown>
  )
}

export default PopoverMenu
